/**
 * Server-side env resolution for Vite SSR / ECS.
 *
 * Priority (first non-empty wins):
 *   1. process.env (ECS task def) — STOREFRONT_* and VITE_* keys
 *   2. Dev defaults — only when NODE_ENV !== 'production'
 *
 * Intentionally does **not** fall back to Vite `define` globals. Those bake
 * empty strings at CI build time and silently mask missing ECS env vars.
 *
 * Platform contract (ECS task definition):
 *   STOREFRONT_BFF_INTERNAL_URL  — preferred BFF URL (internal ALB), never public edge
 *   VITE_STOREFRONT_BFF_URL      — fallback / public BFF URL
 *   STOREFRONT_BASE_URL or VITE_BASE_URL
 *   VITE_FUNNELS_TENANT_ID / TSS_TENANT_ID
 *   VITE_FUNNELS_STORE_ID / TSS_STORE_ID
 *   VITE_CHECKOUT_URL, VITE_CHECKOUT_MIDDLEWARE_URL
 *   VITE_RUNTIME_VERSION
 *   PORT (SSR listen port, default 8080)
 *
 * Production startup: server/index.mjs asserts BFF URL + store/tenant IDs are set.
 */

import { DEV_ENV_DEFAULTS, type EnvConfig } from './env.types';

function pe(...keys: string[]): string {
  if (typeof process === 'undefined' || !process.env) return '';
  for (const key of keys) {
    const v = process.env[key];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return '';
}

function isNodeProduction(): boolean {
  return typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';
}

/**
 * Resolve from process.env only. Dev defaults apply outside production.
 * Never uses Vite define globals.
 */
function pick(processKeys: string[], devDefault: string): string {
  const fromProcess = pe(...processKeys);
  if (fromProcess) return fromProcess;
  if (!isNodeProduction()) return devDefault;
  return '';
}

function dropLoopbackUrlInProduction(value: string): string {
  if (!isNodeProduction()) return value;
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(value) ? '' : value;
}

function sanitizeHttpsImageUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:') return '';
    return trimmed;
  } catch {
    return '';
  }
}

function resolveBffUrl(): string {
  const internal = pe('STOREFRONT_BFF_INTERNAL_URL');
  if (internal) return internal;
  return dropLoopbackUrlInProduction(
    pick(['VITE_STOREFRONT_BFF_URL', 'STOREFRONT_BFF_URL'], DEV_ENV_DEFAULTS.STOREFRONT_BFF_URL),
  );
}

function resolveAuthBffUrl(): string {
  const auth = dropLoopbackUrlInProduction(
    pick(
      ['VITE_STOREFRONT_AUTH_BFF_URL', 'STOREFRONT_AUTH_BFF_URL'],
      DEV_ENV_DEFAULTS.STOREFRONT_AUTH_BFF_URL,
    ),
  );
  return auth || resolveBffUrl();
}

export const env: EnvConfig = {
  PRODUCTS_API_URL: pick(
    ['VITE_PRODUCTS_API_URL', 'PRODUCTS_API_URL'],
    DEV_ENV_DEFAULTS.PRODUCTS_API_URL,
  ),
  CARTS_API_URL: pick(['VITE_CARTS_API_URL', 'CARTS_API_URL'], DEV_ENV_DEFAULTS.CARTS_API_URL),
  CHECKOUT_URL: pick(['VITE_CHECKOUT_URL', 'CHECKOUT_URL'], DEV_ENV_DEFAULTS.CHECKOUT_URL),
  CHECKOUT_MIDDLEWARE_URL: pick(
    ['VITE_CHECKOUT_MIDDLEWARE_URL', 'CHECKOUT_MIDDLEWARE_URL'],
    DEV_ENV_DEFAULTS.CHECKOUT_MIDDLEWARE_URL,
  ),
  CAMPAIGNS_API_URL: pick(
    ['VITE_CAMPAIGNS_API_URL', 'CAMPAIGNS_API_URL'],
    DEV_ENV_DEFAULTS.CAMPAIGNS_API_URL,
  ),
  CAMPAIGNS_TENANT_ID: pick(
    ['VITE_CAMPAIGNS_TENANT_ID', 'CAMPAIGNS_TENANT_ID', 'TSS_TENANT_ID'],
    DEV_ENV_DEFAULTS.CAMPAIGNS_TENANT_ID,
  ),
  FUNNELS_API_URL: pick(['VITE_FUNNELS_API_URL', 'FUNNELS_API_URL'], DEV_ENV_DEFAULTS.FUNNELS_API_URL),
  FUNNELS_TENANT_ID: pick(
    ['VITE_FUNNELS_TENANT_ID', 'FUNNELS_TENANT_ID', 'TSS_TENANT_ID'],
    DEV_ENV_DEFAULTS.FUNNELS_TENANT_ID,
  ),
  FUNNELS_STORE_ID: pick(
    ['VITE_FUNNELS_STORE_ID', 'FUNNELS_STORE_ID', 'TSS_STORE_ID'],
    DEV_ENV_DEFAULTS.FUNNELS_STORE_ID,
  ),
  GTM_ID: pick(['VITE_GTM_ID', 'GTM_ID'], DEV_ENV_DEFAULTS.GTM_ID),
  PUBLIC_PRODUCT_FEED_URL: pick(
    ['VITE_PUBLIC_PRODUCT_FEED_URL', 'PUBLIC_PRODUCT_FEED_URL'],
    DEV_ENV_DEFAULTS.PUBLIC_PRODUCT_FEED_URL,
  ),
  BASE_URL: pick(
    ['STOREFRONT_BASE_URL', 'VITE_BASE_URL', 'BASE_URL'],
    DEV_ENV_DEFAULTS.BASE_URL,
  ),
  STOREFRONT_BFF_URL: resolveBffUrl(),
  STOREFRONT_AUTH_BFF_URL: resolveAuthBffUrl(),
  RUNTIME_VERSION: pick(
    ['VITE_RUNTIME_VERSION', 'RUNTIME_VERSION'],
    DEV_ENV_DEFAULTS.RUNTIME_VERSION,
  ),
  LOGIN_IMAGE_URL: sanitizeHttpsImageUrl(
    pick(['VITE_LOGIN_IMAGE_URL', 'LOGIN_IMAGE_URL'], DEV_ENV_DEFAULTS.LOGIN_IMAGE_URL),
  ),
  CDN_HOSTS: pick(['VITE_CDN_HOSTS', 'CDN_HOSTS'], DEV_ENV_DEFAULTS.CDN_HOSTS),
  CDN_BASE_URL: pick(
    ['VITE_CDN_BASE_URL', 'CDN_BASE_URL'],
    DEV_ENV_DEFAULTS.CDN_BASE_URL,
  ),
};

export const isDev = !isNodeProduction();

export function getApiUrl(apiName: keyof EnvConfig, proxyPath?: string): string {
  if (isDev && proxyPath) return proxyPath;
  return env[apiName];
}

/** Keys required for production SSR — used by server startup assert. */
export const REQUIRED_PROD_SSR_ENV_GROUPS = [
  ['STOREFRONT_BFF_INTERNAL_URL', 'VITE_STOREFRONT_BFF_URL', 'STOREFRONT_BFF_URL'],
  ['TSS_STORE_ID', 'VITE_FUNNELS_STORE_ID', 'FUNNELS_STORE_ID'],
  ['TSS_TENANT_ID', 'VITE_FUNNELS_TENANT_ID', 'FUNNELS_TENANT_ID'],
  ['STOREFRONT_BASE_URL', 'VITE_BASE_URL', 'BASE_URL'],
  ['CDN_BASE_URL', 'VITE_CDN_BASE_URL'],
] as const;

export function missingProdSsrEnvKeys(): string[] {
  const missing: string[] = [];
  for (const group of REQUIRED_PROD_SSR_ENV_GROUPS) {
    if (!pe(...group)) missing.push(group.join('|'));
  }
  return missing;
}
